import axios from 'axios';
import type {
  League,
  Team,
  FixtureSummary,
  MatchComparison,
  TeamMatch,
  InsightsData,
} from './types';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear token and redirect to login (skip in local dev)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (error.response?.status === 401 && !isLocalDev) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

// ─── Leagues ─────────────────────────────────────────────────────────────────

export async function fetchLeagues(): Promise<League[]> {
  const { data } = await api.get<League[]>('/leagues');
  return data;
}

export async function fetchLeagueTeams(leagueId: number): Promise<Team[]> {
  const { data } = await api.get<Team[]>(`/leagues/${leagueId}/teams`);
  return data;
}

export interface LeagueDetail {
  league: League;
  teams: Team[];
  fixtures: FixtureSummary[];
}

export async function fetchLeagueBySlug(slug: string): Promise<LeagueDetail> {
  const { data } = await api.get<LeagueDetail>(`/league/${slug}`);
  return data;
}

// ─── Fixtures & Comparisons ──────────────────────────────────────────────────

export async function fetchFixtures(): Promise<FixtureSummary[]> {
  const { data } = await api.get<FixtureSummary[]>('/comparisons');
  return data;
}

export async function fetchMatchComparison(matchId: number): Promise<MatchComparison> {
  const { data } = await api.get<MatchComparison>(`/matches/${matchId}/comparisons`);
  return data;
}

// ─── Teams ───────────────────────────────────────────────────────────────────

export interface TeamDetail {
  team: Team & { league_name?: string; league_slug?: string; league_logo?: string };
  fixtures: FixtureSummary[];
}

export async function fetchTeamBySlug(slug: string): Promise<TeamDetail> {
  const { data } = await api.get<TeamDetail>(`/team/${slug}`);
  return data;
}

export async function fetchTeamMatches(teamId: number): Promise<TeamMatch[]> {
  const { data } = await api.get<TeamMatch[]>(`/teams/${teamId}/matches`);
  return data;
}

// ─── Search ──────────────────────────────────────────────────────────────────

export interface SearchResultItem {
  id: number;
  name: string;
  slug: string;
  logo: string;
  type: 'team' | 'league';
  league_name?: string;
}

export async function searchTeams(query: string): Promise<SearchResultItem[]> {
  if (query.trim().length < 2) return [];
  const { data } = await api.get<SearchResultItem[]>('/search', { params: { q: query } });
  return data;
}

// ─── Insights ────────────────────────────────────────────────────────────────

export async function fetchInsights(): Promise<InsightsData> {
  const { data } = await api.get<InsightsData>('/insights');
  return data;
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function loginApi(username: string, password: string): Promise<{ token: string }> {
  const { data } = await api.post<{ token: string }>('/login', { username, password });
  return data;
}

export default api;
